// src/features/people/components/UnassignEquipmentsModal.jsx - REFACTORIZADO
import React, { useState, useEffect, useCallback, useRef } from 'react';
import { 
  Modal, 
  Button, 
  Form, 
  Card, 
  Row, 
  Col, 
  ListGroup, 
  Badge,
  Spinner,
  Alert,
  Collapse,
  ButtonGroup
} from 'react-bootstrap';
import { 
  FaTimes, 
  FaArrowLeft, 
  FaCheck,
  FaUser,
  FaBriefcase,
  FaUndo,
  FaTrash,
  FaFlask,
  FaExchangeAlt,
  FaChevronDown,
  FaChevronUp
} from 'react-icons/fa';

// Servicios
import ToolsService from '../../tools/ToolsService';
import PeopleService from '../PeopleService';
import WorksService from '../../works/WorksService';

// Componentes
import CustomSnackbar from '../../../components/CustomSnackbar';

// Constantes y utilidades
import { TIPOS_EQUIPOS, getTipoDescripcion } from '../../../utils/constants';

/**
 * Modal para desasignar equipos con múltiples operaciones
 * Basado en la funcionalidad del componente antiguo UnassignToolsComponent
 */
const UnassignEquipmentsModal = ({ 
  show, 
  onHide, 
  personId, 
  assignedEquipments = [], 
  onEquipmentsUpdate,
  showSnackbar 
}) => {
  // Estados principales
  const [loading, setLoading] = useState(false);
  const [snackbar, setSnackbar] = useState({ open: false, type: '', message: '' });
  
  // Estados para equipos agrupados y selección
  const [groupedTools, setGroupedTools] = useState([]);
  const [openGroups, setOpenGroups] = useState({});
  
  // Estados para modo reasignación
  const [showReassignForm, setShowReassignForm] = useState(false);
  const [reassignType, setReassignType] = useState(''); // 'person' o 'work'
  const [reassignTarget, setReassignTarget] = useState('');
  const [availableTargets, setAvailableTargets] = useState([]);
  const [loadingTargets, setLoadingTargets] = useState(false);

  // Refs para control
  const mountedRef = useRef(true);

  // Control de montaje
  useEffect(() => {
    mountedRef.current = true;
    return () => {
      mountedRef.current = false;
    };
  }, []);

  // Procesar equipos cuando se abre el modal
  useEffect(() => {
    if (show && assignedEquipments.length > 0) {
      processAssignedEquipments();
    }
  }, [show, assignedEquipments]);

  // Función para agrupar equipos por tipo/nombre (similar al GroupByTool del código antiguo)
  const processAssignedEquipments = () => {
    // Agrupar por ID de herramienta
    const groups = {};
    
    assignedEquipments.forEach(equipment => {
      const key = `${equipment.type}-${equipment.name}`;
      
      if (!groups[key]) {
        groups[key] = {
          id: key,
          toolName: equipment.name,
          toolType: equipment.type,
          checked: false,
          indeterminate: false,
          items: []
        };
      }
      
      groups[key].items.push({
        ...equipment,
        checked: false,
        disabled: false
      });
    });

    setGroupedTools(Object.values(groups));
    //console.log('🔧 Equipos agrupados:', Object.values(groups));
  };

  // Función para manejar selección individual
  const handleToggleItem = (groupId, itemId) => {
    setGroupedTools(prev => prev.map(group => {
      if (group.id === groupId) {
        const updatedItems = group.items.map(item => 
          item.id === itemId ? { ...item, checked: !item.checked } : item
        );
        
        const checkedCount = updatedItems.filter(item => item.checked).length;
        const totalCount = updatedItems.length;
        
        return {
          ...group,
          items: updatedItems,
          checked: checkedCount === totalCount,
          indeterminate: checkedCount > 0 && checkedCount < totalCount
        };
      }
      return group;
    }));
  };

  // Función para manejar selección de grupo completo
  const handleToggleGroup = (groupId) => {
    setGroupedTools(prev => prev.map(group => {
      if (group.id === groupId) {
        const newChecked = !group.checked;
        return {
          ...group,
          checked: newChecked,
          indeterminate: false,
          items: group.items.map(item => ({ ...item, checked: newChecked }))
        };
      }
      return group;
    }));
  };

  // Función para toggle de grupos expandidos
  const handleToggleExpand = (groupId) => {
    setOpenGroups(prev => ({
      ...prev,
      [groupId]: !prev[groupId]
    }));
  };

  // Función para obtener equipos seleccionados
  const getSelectedEquipments = () => {
    const selected = [];
    groupedTools.forEach(group => {
      group.items.forEach(item => {
        if (item.checked && !item.disabled) {
          selected.push(item);
        }
      });
    });
    return selected;
  };

  // Función helper para mostrar mensajes
  const showMessage = useCallback((type, message) => {
    if (showSnackbar) {
      showSnackbar(type, message);
    } else {
      setSnackbar({ open: true, type, message });
    }
  }, [showSnackbar]);

  // Función para cargar objetivos de reasignación
  const loadReassignTargets = async (type) => {
    try {
      setLoadingTargets(true);
      let targets = [];

      if (type === 'person') {
        const people = await PeopleService.getAllPeople();
        targets = people
          .filter(person => person.type === 'F' && person.id !== personId)
          .map(person => ({
            id: person.id,
            name: `${person.firstName} ${person.surname}`,
            subtitle: person.document
          }));
      } else if (type === 'work') {
        const works = await WorksService.getAllWorks();
        targets = works
          .filter(work => work.id !== personId)
          .map(work => ({
            id: work.id,
            name: work.name,
            subtitle: work.location || ''
          }));
      }

      setAvailableTargets(targets);
    } catch (error) {
      console.error('❌ Error al cargar objetivos:', error);
      showMessage('error', 'Error al cargar los objetivos de reasignación');
    } finally {
      setLoadingTargets(false);
    }
  };

  // Manejar cambio de tipo de reasignación
  const handleReassignTypeChange = (type) => {
    setReassignType(type);
    setReassignTarget('');
    if (type) {
      loadReassignTargets(type);
    }
  };

  // Función para realizar operaciones en lote
  const performBulkOperation = async (operation) => {
    const selectedEquipments = getSelectedEquipments();
    
    if (selectedEquipments.length === 0) {
      showMessage('warning', 'Por favor seleccione al menos un equipo');
      return;
    }

    const confirmMessages = {
      unassign: '¿Está seguro de devolver los equipos seleccionados al stock?',
      delete: '¿Está seguro de dar de baja definitivamente los equipos seleccionados?',
      retire: '¿Está seguro de retirar los equipos seleccionados para pruebas?',
      reassign: `¿Está seguro de reasignar los equipos seleccionados?`
    };

    if (!window.confirm(confirmMessages[operation])) {
      return;
    }

    try {
      setLoading(true);
      
      const promises = selectedEquipments.map(async (equipment) => {
        let result;
        switch (operation) {
          case 'unassign':
            result = await ToolsService.unassignTool(equipment.id);
            break;
          case 'delete':
            result = await ToolsService.deleteAssignedTool(equipment.id);
            break;
          case 'retire':
            result = await ToolsService.retireAssignedTool(equipment.id);
            break;
          case 'reassign':
            if (reassignType === 'person') {
              result = await ToolsService.reassignToolToPerson(reassignTarget, equipment.id);
            } else if (reassignType === 'work') {
              result = await ToolsService.reassignToolToWork(reassignTarget, equipment.id);
            }
            break;
          default:
            throw new Error('Operación no válida');
        }
        
        // Verificar si la operación fue exitosa
        if (result?.statusCode && result.statusCode >= 400) {
          throw new Error(`Error en equipo ${equipment.name}: ${result.data?.message || 'Error desconocido'}`);
        }
        
        return result;
      });

      const results = await Promise.all(promises);

      // Marcar como deshabilitados los equipos procesados
      setGroupedTools(prev => prev.map(group => ({
        ...group,
        items: group.items.map(item => 
          selectedEquipments.some(selected => selected.id === item.id)
            ? { ...item, disabled: true, checked: false }
            : item
        )
      })));

      const operationMessages = {
        unassign: 'Equipos devueltos al stock correctamente',
        delete: 'Equipos dados de baja correctamente',
        retire: 'Equipos retirados para pruebas correctamente',
        reassign: 'Equipos reasignados correctamente'
      };

      showMessage('success', operationMessages[operation]);

      // Cerrar formulario de reasignación si estaba abierto
      if (operation === 'reassign') {
        setShowReassignForm(false);
        setReassignType('');
        setReassignTarget('');
      }

      // Notificar al componente padre
      if (onEquipmentsUpdate) {
        onEquipmentsUpdate();
      }

      // Cerrar modal después de un breve delay
      setTimeout(() => {
        if (mountedRef.current) {
          handleCloseModal();
        }
      }, 2000);

    } catch (error) {
      console.error(`❌ Error en operación ${operation}:`, error);
      showMessage('error', `Error al ${operation === 'unassign' ? 'devolver' : operation === 'delete' ? 'dar de baja' : operation === 'retire' ? 'retirar' : 'reasignar'} equipos`);
    } finally {
      setLoading(false);
    }
  };

  // Función para cerrar el modal
  const handleCloseModal = () => {
    setGroupedTools([]);
    setOpenGroups({});
    setShowReassignForm(false);
    setReassignType('');
    setReassignTarget('');
    setAvailableTargets([]);
    onHide();
  };

  // Función para cerrar snackbar interno
  const handleCloseSnackbar = () => {
    setSnackbar(prev => ({ ...prev, open: false }));
  };

  // Verificar si hay equipos seleccionados
  const hasSelectedEquipments = getSelectedEquipments().length > 0;

  return (
    <>
      <Modal
        show={show}
        onHide={handleCloseModal}
        size="lg"
        centered
        backdrop="static"
        keyboard={false}
      >
        <Modal.Header closeButton>
          <Modal.Title className="d-flex align-items-center">
            {showReassignForm ? (
              <>
                <FaExchangeAlt className="me-2" />
                Reasignar Equipos
              </>
            ) : (
              <>
                <FaUndo className="me-2" />
                Gestionar Equipos Asignados
              </>
            )}
          </Modal.Title>
        </Modal.Header>

        <Modal.Body>
          {loading ? (
            <div className="text-center my-4">
              <Spinner animation="border" role="status">
                <span className="visually-hidden">Procesando...</span>
              </Spinner>
              <p className="mt-2">Procesando operación...</p>
            </div>
          ) : (
            <>
              {/* Formulario de Reasignación */}
              <Collapse in={showReassignForm}>
                <Card className="mb-3">
                  <Card.Header>
                    <div className="d-flex align-items-center justify-content-between">
                      <h6 className="mb-0">Reasignar a:</h6>
                      <Button
                        variant="outline-secondary"
                        size="sm"
                        onClick={() => setShowReassignForm(false)}
                      >
                        <FaTimes />
                      </Button>
                    </div>
                  </Card.Header>
                  <Card.Body>
                    <Row>
                      <Col md={4}>
                        <Form.Group className="mb-3">
                          <Form.Label>Tipo de destino</Form.Label>
                          <div>
                            <Form.Check
                              type="radio"
                              name="reassignType"
                              id="reassign-person"
                              label="Persona"
                              value="person"
                              checked={reassignType === 'person'}
                              onChange={(e) => handleReassignTypeChange('person')}
                            />
                            <Form.Check
                              type="radio"
                              name="reassignType"
                              id="reassign-work"
                              label="Obra"
                              value="work"
                              checked={reassignType === 'work'}
                              onChange={(e) => handleReassignTypeChange('work')}
                            />
                          </div>
                        </Form.Group>
                      </Col>
                      <Col md={6}>
                        <Form.Group className="mb-3">
                          <Form.Label>Seleccionar destino</Form.Label>
                          <Form.Select
                            value={reassignTarget}
                            onChange={(e) => setReassignTarget(e.target.value)}
                            disabled={!reassignType || loadingTargets}
                          >
                            <option value="">
                              {loadingTargets ? 'Cargando...' : 'Seleccione destino...'}
                            </option>
                            {availableTargets.map(target => (
                              <option key={target.id} value={target.id}>
                                {target.name} {target.subtitle && `- ${target.subtitle}`}
                              </option>
                            ))}
                          </Form.Select>
                        </Form.Group>
                      </Col>
                      <Col md={2}>
                        <Form.Group>
                          <Form.Label>&nbsp;</Form.Label>
                          <div>
                            <Button
                              variant="success"
                              disabled={!reassignTarget || !hasSelectedEquipments}
                              onClick={() => performBulkOperation('reassign')}
                              className="w-100"
                            >
                              <FaCheck className="me-1" />
                              Confirmar
                            </Button>
                          </div>
                        </Form.Group>
                      </Col>
                    </Row>
                  </Card.Body>
                </Card>
              </Collapse>

              {/* Lista de Equipos Agrupados */}
              <div style={{ maxHeight: '400px', overflowY: 'auto' }}>
                {groupedTools.length === 0 ? (
                  <Alert variant="info">
                    No hay equipos asignados para gestionar.
                  </Alert>
                ) : (
                  groupedTools.map(group => (
                    <Card key={group.id} className="mb-2">
                      <Card.Header 
                        className="py-2 cursor-pointer"
                        onClick={() => handleToggleExpand(group.id)}
                        style={{ cursor: 'pointer' }}
                      >
                        <div className="d-flex align-items-center justify-content-between">
                          <div className="d-flex align-items-center">
                            <Form.Check
                              type="checkbox"
                              checked={group.checked}
                              onChange={(e) => {
                                e.stopPropagation();
                                handleToggleGroup(group.id);
                              }}
                              ref={input => {
                                if (input) input.indeterminate = group.indeterminate;
                              }}
                              className="me-2"
                            />
                            <div>
                              <strong>{group.toolName}</strong>
                              <Badge bg="secondary" className="ms-2">
                                {getTipoDescripcion(group.toolType, TIPOS_EQUIPOS)}
                              </Badge>
                            </div>
                          </div>
                          <div className="d-flex align-items-center">
                            <Badge bg="primary" className="me-2">
                              {group.items.filter(item => !item.disabled).length} disponibles
                            </Badge>
                            {openGroups[group.id] ? <FaChevronUp /> : <FaChevronDown />}
                          </div>
                        </div>
                      </Card.Header>
                      
                      <Collapse in={openGroups[group.id]}>
                        <Card.Body className="py-2">
                          <ListGroup variant="flush">
                            {group.items
                              .filter(item => !item.disabled)
                              .map(item => (
                                <ListGroup.Item 
                                  key={item.id} 
                                  className="py-2 d-flex align-items-center justify-content-between"
                                >
                                  <div className="d-flex align-items-center">
                                    <Form.Check
                                      type="checkbox"
                                      checked={item.checked}
                                      onChange={() => handleToggleItem(group.id, item.id)}
                                      disabled={item.disabled}
                                      className="me-2"
                                    />
                                    <div>
                                      <span>{item.name}</span>
                                      {item.serial && (
                                        <small className="text-muted ms-2">
                                          Serial: <code>{item.serial}</code>
                                        </small>
                                      )}
                                    </div>
                                  </div>
                                  <Badge bg="success">
                                    Cantidad: {item.quantity || 1}
                                  </Badge>
                                </ListGroup.Item>
                              ))
                            }
                          </ListGroup>
                        </Card.Body>
                      </Collapse>
                    </Card>
                  ))
                )}
              </div>
            </>
          )}
        </Modal.Body>

        <Modal.Footer>
          <div className="d-flex justify-content-between w-100">
            <Button variant="secondary" onClick={handleCloseModal}>
              <FaArrowLeft className="me-1" /> Volver
            </Button>
            
            <ButtonGroup>
              <Button
                variant={showReassignForm ? "secondary" : "primary"}
                onClick={() => setShowReassignForm(!showReassignForm)}
                disabled={loading || !hasSelectedEquipments}
              >
                <FaExchangeAlt className="me-1" />
                {showReassignForm ? "Cancelar" : "Reasignar"}
              </Button>
              
              <Button
                variant="info"
                onClick={() => performBulkOperation('unassign')}
                disabled={loading || !hasSelectedEquipments || showReassignForm}
              >
                <FaUndo className="me-1" />
                Volver a Stock
              </Button>
              
              <Button
                variant="warning"
                onClick={() => performBulkOperation('retire')}
                disabled={loading || !hasSelectedEquipments || showReassignForm}
              >
                <FaFlask className="me-1" />
                Retirar para Pruebas
              </Button>
              
              <Button
                variant="danger"
                onClick={() => performBulkOperation('delete')}
                disabled={loading || !hasSelectedEquipments || showReassignForm}
              >
                <FaTrash className="me-1" />
                Baja Definitiva
              </Button>
            </ButtonGroup>
          </div>
        </Modal.Footer>
      </Modal>

      {/* Snackbar interno (solo si no se proporciona showSnackbar) */}
      {!showSnackbar && (
        <CustomSnackbar
          open={snackbar.open}
          type={snackbar.type}
          message={snackbar.message}
          onClose={handleCloseSnackbar}
        />
      )}
    </>
  );
};

export default UnassignEquipmentsModal;